// Derivation of the app's current active view from React Router state.
//
// This is a shared helper so both ViewManagerContext (which sets the value
// consumed by view-manager clients under its provider) and App.tsx (which
// sits ABOVE the provider and needs the same signal for the tournament-
// subscribe set) share ONE definition. Keeping the derivation in one place
// prevents the two callers from drifting out of sync — e.g. one classifying
// `/grid/some-suffix` as `grid` while the other classifies it as `lobby`,
// which was the failure mode this file exists to prevent.

export type ActiveView = 'lobby' | 'table' | 'grid';

/**
 * Compute the active view from a URL pathname + the router's table-route
 * match (which callers already have via `useMatch('/table/:gameID/:gameType')`).
 *
 *   • table  ← the /table/:gameID/:gameType route is matched
 *   • grid   ← pathname begins with `/grid` (any suffix — `/grid`,
 *              `/grid/`, `/grid/whatever`). Strict-equality caused a bug
 *              where a trailing slash silently classified as `lobby` and
 *              dropped every tournament subscribe.
 *   • lobby  ← otherwise
 *
 * Accepts the tableMatch value directly (rather than re-running `useMatch`
 * here) so callers keep control of the hook order in their components.
 */
export function deriveActiveView(
  pathname: string | undefined,
  tableMatch: { params?: { gameID?: string; gameType?: string } } | null | undefined
): ActiveView {
  if (tableMatch) return 'table';
  if (typeof pathname === 'string' && pathname.startsWith('/grid')) return 'grid';
  return 'lobby';
}
